import React from 'react';
import { Button, Form, Input, Popconfirm, Select, Switch, Tooltip, TreeSelect } from 'antd';
import { openNotification } from '../../../utils';
import { listReportNodesAll } from '../../report-nodes/services/report-nodeService';
import { createReportNode, updateReportNode, getReportNode } from '../services/report-nodeService';
import { CreateReportNodeDto } from '../types/ReportNode';
import { listMeasuringInstrumentsAll } from '../../measuring-instruments/services/measuring-instrumentService';
import { getReportFormat } from '../../report-formats/services/report-formatService';
import { apiFetch } from 'services/apiClient';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';

const getParentReferenceId = (node: any) => {
  const parent = node?.parent;
  return typeof parent === 'object' ? parent?.id : parent;
};

const buildParentTreeData = (nodes: any[], currentId?: number) => {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];
  const map = new Map<number, any>();
  const roots: any[] = [];

  const createEntry = (node: any) => {
    const label =
      node?.title ??
      node?.code ??
      `#${node?.id}`;
    return {
      id: node.id,
      title: label,
      value: node.id,
      key: node.id,
      disabled: currentId !== undefined && node.id === currentId,
      children: [],
      sortOrder: Number.isFinite(Number(node?.sortOrder)) ? Number(node.sortOrder) : 0,
    };
  };

  const sortedNodes = [...nodes].filter(node => typeof node?.id === 'number');
  sortedNodes.forEach(node => {
    map.set(node.id, createEntry(node));
  });

  sortedNodes.forEach(node => {
    const entry = map.get(node.id);
    if (!entry) return;
    const parentId = getParentReferenceId(node);
    if (parentId != null && map.has(parentId)) {
      map.get(parentId).children.push(entry);
    } else {
      roots.push(entry);
    }
  });

  const sortNodes = (items: any[]) => {
    items.sort((a, b) => {
      const orderDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return (a.id ?? 0) - (b.id ?? 0);
    });
    items.forEach(item => {
      if (Array.isArray(item.children) && item.children.length > 0) {
        sortNodes(item.children);
      }
    });
  };

  sortNodes(roots);
  return roots;
};

const formatFilterValue = (value?: string) => value?.toLocaleLowerCase('tr') ?? '';

const measuringInstrumentFieldOptions = [
  { value: 'INVENTORY_NO', label: 'ID = Envanter No' },
  { value: 'INSTRUMENT_NAME', label: 'Ölçüm Cihazı Adı' },
  { value: 'SERIAL_NUMBER', label: 'Seri Numarası' },
  { value: 'BRAND', label: 'Marka' },
  { value: 'MODEL', label: 'Model' },
  { value: 'CALIBRATION_NUMBER', label: 'Kalibrasyon Numarası' },
  { value: 'CALIBRATION_DATE', label: 'Kalibrasyon Tarihi' },
  { value: 'CALIBRATION_VALID_UNTIL', label: 'Kalibrasyon Geçerlilik Tarihi' },
  { value: 'CALIBRATION_ORGANIZATION', label: 'Kalibrasyon Kuruluşu' },
];

export default function ReportNodeForm({ id, parentId, onSuccess, referenceId }: { id?: number; parentId?: number; onSuccess: () => void; referenceId?: number }) {
  const [form] = Form.useForm<CreateReportNodeDto>();
  const [reportNodeOptions, setReportNodeOptions] = React.useState<any[]>([]);
  const [measuringInstrumentOptions, setMeasuringInstrumentOptions] = React.useState<any[]>([]);
  const [activeControlAreaOptions, setActiveControlAreaOptions] = React.useState<any[]>([]);
  const [inactiveControlAreaOptions, setInactiveControlAreaOptions] = React.useState<any[]>([]);
  const [controlAreasLoading, setControlAreasLoading] = React.useState(false);
  const [sortOrderTouched, setSortOrderTouched] = React.useState(false);
  const [formatAttributeId, setFormatAttributeId] = React.useState<number | undefined>(undefined);
  const nodeType = Form.useWatch('type', form);
  const selectedControlAreas = Form.useWatch('controlAreas', form);
  const typeOptions = React.useMemo(() => [
    { value: 'PERIODIC_CONTROL_METHOD', label: 'Periyodik Kontrol Metodu' },
    { value: 'NOTE', label: 'Not' },
    { value: 'DEFECT', label: 'Kusur' },
    { value: 'MEASURING_INSTRUMENT', label: 'Ölçüm Cihazı' },
    { value: 'CONTROL_CRITERIA_AND_TESTS', label: 'Kontrol Kriteri ve Testler' },
    { value: 'STANDARD', label: 'Varsayılan' },
    { value: 'PANEL_CONTROL', label: 'Pano Kontrol' },
    { value: 'APPROVAL', label: 'Onay' },
    { value: 'RESULT', label: 'Sonuç ve Kanaat' },
    { value: 'rcd', label: 'RCD' },
    { value: 'earthingInformation', label: 'Topraklama' },
  ], []);
  React.useEffect(() => {
    let cancelled = false;
    if (!referenceId) {
      setFormatAttributeId(undefined);
      setMeasuringInstrumentOptions([]);
      return;
    }
    getReportFormat(referenceId)
      .then(format => {
        if (cancelled) return;
        const attrId =
          typeof format?.attribute === 'object'
            ? format.attribute?.id
            : format?.attribute;
        setFormatAttributeId(attrId ?? undefined);
      })
      .catch(() => {
        if (!cancelled) {
          setFormatAttributeId(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [referenceId]);
  React.useEffect(() => {
    if (formatAttributeId == null) {
      setMeasuringInstrumentOptions([]);
      return;
    }
    let cancelled = false;
    listMeasuringInstrumentsAll('attributesId.equals=' + formatAttributeId)
      .then(data => {
        if (!cancelled) {
          setMeasuringInstrumentOptions(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMeasuringInstrumentOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [formatAttributeId]);
  React.useEffect(() => {
    if (formatAttributeId == null) {
      setActiveControlAreaOptions([]);
      setInactiveControlAreaOptions([]);
      setControlAreasLoading(false);
      return;
    }
    let cancelled = false;
    setControlAreasLoading(true);
    Promise.all([
      apiFetch(`/control-areas?attributeId=${formatAttributeId}`),
      apiFetch(`/control-areas?attributeId=${formatAttributeId}&isActive=false`),
    ])
      .then(([activeResponse, inactiveResponse]: any[]) => {
        if (cancelled) return;
        const normalize = (response: any) => Array.isArray(response) ? response : response?.data ?? [];
        setActiveControlAreaOptions(normalize(activeResponse));
        setInactiveControlAreaOptions(normalize(inactiveResponse));
      })
      .catch(() => {
        if (!cancelled) {
          setActiveControlAreaOptions([]);
          setInactiveControlAreaOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setControlAreasLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [formatAttributeId]);
  React.useEffect(() => {
    setSortOrderTouched(false);
    if (id) {
      getReportNode(id).then(d => {
        const data: any = { ...d };
        if (data.parent) data.parent = typeof data.parent === 'object' ? data.parent.id : data.parent;
        if (!data.type) data.type = 'STANDARD';
        if (['rcd', 'earthing_information'].includes(String(data.type).trim().toLowerCase())) {
          data.type = 'rcd';
        } else if (['earthinginformation', 'earthing'].includes(String(data.type).trim().toLowerCase())) {
          data.type = 'earthingInformation';
        }
        data.showInReportForm = data.showInReportForm ?? true;
        data.showInReport = data.showInReport ?? true;
        if (Array.isArray(data.measuringInstruments)) {
          data.measuringInstruments = data.measuringInstruments.map((mi: any) => typeof mi === 'object' ? mi.id : mi).filter((mi: any) => mi !== undefined && mi !== null);
        }
        if (Array.isArray(data.controlAreas)) {
          data.controlAreas = data.controlAreas
            .map((controlArea: any) => typeof controlArea === 'object' ? controlArea?.id : controlArea)
            .filter((controlArea: any) => controlArea !== undefined && controlArea !== null);
        }
        if (!Array.isArray(data.measuringInstrumentFields)) {
          data.measuringInstrumentFields = [];
        }
        form.setFieldsValue(data);
      });
    } else {
      form.resetFields();
      const initialValues: any = { type: 'STANDARD' };
      if (parentId) initialValues.parent = parentId;
      initialValues.measuringInstruments = [];
      initialValues.measuringInstrumentFields = [];
      initialValues.controlAreas = [];
      initialValues.reportDescription = null;
      initialValues.showInReportForm = true;
      initialValues.showInReport = true;
      form.setFieldsValue(initialValues);
    }
  }, [id, parentId, form]);
  React.useEffect(() => {
    listReportNodesAll(referenceId !== undefined ? 'reportFormatId.equals=' + referenceId : '')
      .then(data => {
        const items = Array.isArray(data) ? data : [];
        setReportNodeOptions(items);
        const maxSortOrder = items.reduce((max, node) => {
          const value = typeof node.sortOrder === 'number' ? node.sortOrder : Number(node.sortOrder);
          if (!Number.isFinite(value)) return max;
          return value > max ? value : max;
        }, -1);
        if (!id && !sortOrderTouched) {
          form.setFieldsValue({ sortOrder: maxSortOrder >= 0 ? maxSortOrder + 1 : 0 });
        }
      })
      .catch(() => {
        setReportNodeOptions([]);
        if (!id && !sortOrderTouched) {
          form.setFieldsValue({ sortOrder: 0 });
        }
      });
  }, [referenceId, id, sortOrderTouched, form]);
  const parentTreeData = React.useMemo(() => buildParentTreeData(reportNodeOptions, id), [reportNodeOptions, id]);
  const controlAreaOptions = React.useMemo(() => {
    const selectedIds = new Set(
      (Array.isArray(selectedControlAreas) ? selectedControlAreas : [])
        .map((controlArea: any) => Number(typeof controlArea === 'object' ? controlArea?.id : controlArea))
        .filter((controlAreaId: number) => Number.isFinite(controlAreaId))
    );
    const active = activeControlAreaOptions.map((controlArea: any) => ({
      value: controlArea.id,
      label: controlArea.name ?? controlArea.id,
    }));
    const activeIds = new Set(active.map((controlArea: any) => Number(controlArea.value)));
    const inactiveSelected = inactiveControlAreaOptions
      .filter((controlArea: any) => selectedIds.has(Number(controlArea.id)) && !activeIds.has(Number(controlArea.id)))
      .map((controlArea: any) => ({
        value: controlArea.id,
        label: controlArea.name ?? controlArea.id,
        disabled: true,
      }));
    return [...active, ...inactiveSelected];
  }, [activeControlAreaOptions, inactiveControlAreaOptions, selectedControlAreas]);
  const onFinish = async (values: CreateReportNodeDto) => {
    const data: any = { ...values };
    if (values.controlAreas !== undefined) {
      data.controlAreas = Array.isArray(values.controlAreas)
        ? values.controlAreas
            .map((controlArea: any) => Number(typeof controlArea === 'object' ? controlArea?.id : controlArea))
            .filter((controlAreaId: number) => Number.isFinite(controlAreaId))
        : [];
    }
    if (referenceId !== undefined) data.reportFormat = referenceId;
    try {
      if (id) {
        await updateReportNode(id, data);
        openNotification('success', 'Başarılı', 'Bölüm Güncellendi');
      } else {
        await createReportNode(data);
        openNotification('success', 'Başarılı', 'Bölüm Eklendi');
      }
      onSuccess();
    } catch (e) {
      openNotification('error', 'Hata', 'Bölüm işlemi başarısız');
    }
  };
  return (
    <>

      <Form form={form} onFinish={onFinish} layout='vertical'>
        <Form.Item name='title' label='Başlık' rules={[{ required: true }]}>
          <Input style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='code' label='Kod'>
          <Input style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='description' label='Açıklama'>
          <Input.TextArea rows={3} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='reportDescription'
          label={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span>
                Rapor Açıklaması{' '}
                <Tooltip title='Raporda gözükecek'>
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
              <Popconfirm
                title='Rapor açıklamasını silmek istediğinize emin misiniz?'
                okText='Sil'
                cancelText='Vazgeç'
                okButtonProps={{ danger: true }}
                onConfirm={() => form.setFieldsValue({ reportDescription: null })}
              >
                <Button danger type='text' size='small' htmlType='button' aria-label='Rapor açıklamasını sil' icon={<DeleteOutlined />}>
                  Sil
                </Button>
              </Popconfirm>
            </span>
          }
          getValueProps={(value) => ({ value: value ?? '' })}
          getValueFromEvent={(content, _delta, source) =>
            source === 'user' ? content : form.getFieldValue('reportDescription')
          }
        >
          <ReactQuill theme='snow' />
        </Form.Item>
        <Form.Item name='type' label='Tür' rules={[{ required: true, message: 'Lütfen bölüm türünü seçiniz' }]}>
          <Select
            placeholder='Seçiniz'
            options={typeOptions}
            onChange={(value) => {
              if (value !== 'MEASURING_INSTRUMENT') {
                form.setFieldsValue({ measuringInstruments: [], measuringInstrumentFields: [] });
              } else if (!form.getFieldValue('measuringInstruments')) {
                form.setFieldsValue({ measuringInstruments: [], measuringInstrumentFields: form.getFieldValue('measuringInstrumentFields') ?? [] });
              }
            }}
          />
        </Form.Item>
        <Form.Item name='controlAreas' label='Parametre Tipleri - Özellikleri'>
          <Select
            mode='multiple'
            placeholder='Seçiniz'
            options={controlAreaOptions}
            allowClear
            showSearch
            optionFilterProp='label'
            loading={controlAreasLoading}
            disabled={formatAttributeId == null || controlAreasLoading}
            filterOption={(input, option) => {
              const label = String(option?.label ?? '');
              return formatFilterValue(label).includes(formatFilterValue(input));
            }}
          />
        </Form.Item>
        {nodeType === 'MEASURING_INSTRUMENT' && (
          <>
            <Form.Item
              name='measuringInstrumentFields'
              label='Ölçüm Cihazı Bilgileri'
              rules={[{ required: true, type: 'array', min: 1, message: 'Lütfen en az bir alan seçiniz' }]}
            >
              <Select
                mode='multiple'
                placeholder='Rapor kolonlarını seçiniz'
                options={measuringInstrumentFieldOptions}
                allowClear
                showSearch
                optionFilterProp='label'
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '');
                  return formatFilterValue(label).includes(formatFilterValue(input));
                }}
              />
            </Form.Item>
            <Form.Item name='measuringInstruments' label='Ölçüm Cihazları'>
              <Select
                mode='multiple'
                placeholder='Ölçüm cihazı seçiniz'
                options={measuringInstrumentOptions.map(mi => ({ value: mi.id, label: mi.name ?? mi.id }))}
                allowClear
                showSearch
                optionFilterProp='label'
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '');
                  return formatFilterValue(label).includes(formatFilterValue(input));
                }}
              />
            </Form.Item>
          </>
        )}
        <Form.Item
          name='showInReportForm'
          label='Rapor Veri Giriş Formunda Gözüksün'
          valuePropName='checked'
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name='showInReport'
          label='Raporda Gözüksün'
          valuePropName='checked'
        >
          <Switch />
        </Form.Item>
        <Form.Item name='parent' label='Üst Bölüm'>
          <TreeSelect
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400 }}
            placeholder='Seçiniz'
            allowClear
            showSearch
            treeDefaultExpandAll
            treeNodeFilterProp='title'
            filterTreeNode={(input, treeNode) => {
              if (!input) return true;
              const title = typeof treeNode?.title === 'string' ? treeNode.title : '';
              return title.toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'));
            }}
            treeData={parentTreeData}
          />
        </Form.Item>
        <Button htmlType='submit' type='primary' block>Kaydet</Button>
      </Form>
    </>
  );
}
